import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useState,
} from 'react';
import { useForm } from 'react-hook-form-new';
import { QueryObserverResult, useQueryClient } from '@tanstack/react-query';
import { useSnackbar as useSnackbarComponents } from '@nextiva/next-ui-components';
import { isEqual } from 'lodash';
import { AxiosError } from 'axios';
import {
  FormValuesV3,
  IWizardContextPropsV3,
  EnablementInfoPayloadV3,
  FileAttachments,
  IRequestWebsiteForm,
} from 'interface/WizardV3';
import { useSnackbar, useUpdateCompanyTaxId } from 'hooks';
import {
  defaultValuesWizardV3,
  ENABLEMENT_INFO_KEY,
  STEPSLIST,
} from 'containers/pages/Messaging/CampaignDialog/constants';
import { AttachmentType } from 'services';
import { actionTypes, RootContext } from 'RootContext/rootContext';
import {
  ATTACHMENTS_ERROR,
  ATTACHMENTS_IN_PROGRESS,
  ATTACHMENTS_SUCCESS,
} from 'components/Snackbar/const';
import {
  ALLOWED_MIME_TYPES_MMS,
  ALLOWED_MIME_TYPES_SUPPORTDOCS,
  FILE_MAX_SIZE,
  SMS_CAMPAIGN_STATUS,
} from '../constants';
import { useGetEnablementInfo } from './useGetEnablementInfo';
import { reducer, initialState, actions } from './reducerWizard';
import { useCreateEnablementInfo } from './useCreateEnablementInfo';
import { useUpdateEnablementInfo } from './useUpdateEnablementInfo';
import { useCreateTCRBrandAndCampaign } from './useCreateTCRBrandAndCampaign';
import { useGetSMSCampaignStatus } from './useGetSMSCampaignStatus';
import { useUpdateFileAttachments } from './useUpdateFileAttachments';
import { useGetFileAttachments } from './useGetFileAttachments';
import { useDeleteFileAttachments } from './useDeleteFileAttachments';
import { useGetSMSCampaignInfo } from './useGetSMSCampaignInfo';
import { useRequestWebsiteCreation } from './useRequestWebsiteCreation';

const WizardContextV3 = createContext<IWizardContextPropsV3>(null);
export function useWizardContextV3() {
  const context = useContext(WizardContextV3);
  if (!context) {
    throw new Error(
      'useWizardContextV3 must be used within a WizardV3Provider',
    );
  }
  return context;
}
const errorMessage = 'Something went wrong, please try again later.';

export const WizardProviderV3 = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const methods = useForm<FormValuesV3>({
    mode: 'all',
    defaultValues: defaultValuesWizardV3,
  });
  const [filesSupportDocs, setFilesSupportDocs] = useState<FileAttachments[]>(
    [],
  );
  const [resetForm, setResetForm] = useState<boolean>(false);
  const [filesMMS, setFilesMMS] = useState<FileAttachments[]>([]);
  const [fileErrors, setFileErrors] = useState<string[]>([]);
  const totalFiles = 5;
  const [state, dispatch] = useReducer(reducer, initialState);
  const [values, setValues] = useState<Record<string, any>>({});
  const [prevSubUseCases, setPrevSubUseCases] = useState<string>(null);
  const {
    enablementInfo,
    isLoadingGetEnablementInfo,
    refetch,
  } = useGetEnablementInfo(methods);
  const {
    data: smsCampaignInformation,
    refetch: refetchCampaignInformation,
  } = useGetSMSCampaignInfo();
  const [
    isWebsiteRequestEmailSent,
    setIsWebsiteRequestEmailSent,
  ] = useState<boolean>(
    enablementInfo ? enablementInfo.emailWebsiteRequestSent : false,
  );

  const { rootDispatch } = useContext(RootContext);

  const closeSnackbar = () => {
    rootDispatch({
      type: actionTypes.CLOSE_GLOBAL_SNACKBAR,
    });
  };

  const {
    data: smsCampaignStatus,
    refetch: refetchSMSCampaignStatus,
  } = useGetSMSCampaignStatus();
  const isDeclined = smsCampaignStatus === SMS_CAMPAIGN_STATUS.DECLINED;
  const declinedWithBrandVerified =
    smsCampaignStatus === SMS_CAMPAIGN_STATUS.DECLINED &&
    Array.isArray(smsCampaignInformation?.data) &&
    smsCampaignInformation?.data.some((campaign) => !!campaign.campaignId);
  useEffect(() => {
    if (declinedWithBrandVerified) {
      dispatch(actions.setStep(1));
    }
  }, [declinedWithBrandVerified]);
  const doCampaignRefetch = useCallback(() => {
    void refetchSMSCampaignStatus();
    void refetchCampaignInformation();
  }, [refetchCampaignInformation, refetchSMSCampaignStatus]);
  const { enqueueSnackbar } = useSnackbarComponents();
  const requestWebsiteCreation = useRequestWebsiteCreation({ enqueueSnackbar });
  const createTCRBrandAndCampaign = useCreateTCRBrandAndCampaign();
  const createEnablementInfo = useCreateEnablementInfo();
  const updateEnablementInfo = useUpdateEnablementInfo();
  const queryClient = useQueryClient();
  const updateCompanyTaxId = useUpdateCompanyTaxId();
  const controller = new AbortController();
  const setSnackbar = useSnackbar();
  const deleteFileAttachments = useDeleteFileAttachments();
  const updateFileSupportingDocs = useUpdateFileAttachments(
    'supportingDocument',
    controller,
  );
  const { fileAttachments: fileAttachmentsSupportDocs } = useGetFileAttachments(
    'supportingDocument',
  );
  const handleSendEmail = (websiteformData: IRequestWebsiteForm) => {
    if (isWebsiteRequestEmailSent) {
      enqueueSnackbar('Website request already submitted!');
      return;
    }
    requestWebsiteCreation.mutate(websiteformData);
    setIsWebsiteRequestEmailSent(true);
  };
  const { fileAttachments: fileAttachmentsMMS } = useGetFileAttachments('mms');
  const updateFileMMS = useUpdateFileAttachments('mms', controller);

  useEffect(() => {
    if (Array.isArray(fileAttachmentsSupportDocs?.data)) {
      setFilesSupportDocs(fileAttachmentsSupportDocs.data);
    }
  }, [fileAttachmentsSupportDocs]);

  useEffect(() => {
    if (Array.isArray(fileAttachmentsMMS?.data)) {
      setFilesMMS(fileAttachmentsMMS.data);
    }
  }, [fileAttachmentsMMS]);

  useEffect(() => {
    if (resetForm) {
      methods.reset({
        companyType: null,
      });
      setFilesSupportDocs([]);
      setFilesMMS([]);
      setResetForm(false);
    }
  }, [resetForm]);

  const handleFileSelected = useCallback(
    (file = null, attachmentType: AttachmentType) => {
      if (file === null) return;
      function showSuccessSnackbar() {
        setSnackbar({
          actionType: ATTACHMENTS_SUCCESS,
          actionLabel: 'View',
          message: file.name,
          severity: 'info',
          onAction: () => null,
        });
      }
      function showErrorSnackbar() {
        setSnackbar({
          actionType: ATTACHMENTS_ERROR,
          actionLabel: 'Retry',
          message: file.name,
          severity: 'error',
          onAction: () => handleFileSelected(file, attachmentType),
        });
      }

      const errors = [];
      const ALLOWED_MIME_TYPES =
        attachmentType === 'mms'
          ? ALLOWED_MIME_TYPES_MMS
          : ALLOWED_MIME_TYPES_SUPPORTDOCS;

      const isAllowedType = ALLOWED_MIME_TYPES.some(
        (mineType) => file.type === mineType,
      );
      const allowedExtensionsMms = /(\.3ga|\.3gp|\.amr|\.avi|\.bmp|\.bz2|\.dib|\.flac|\.flv|\.jpeg|\.jpg|\.m2a|\.m4a|\.m4b|\.m4p|\.m4r|\.m4v|\.mp1|\.mp2|\.mp3|\.mpa|\.oga|\.ogg|\.ogm|\.ogv|\.ogx|\.png|\.spx|\.txt|\.wav|\.webm)$/i;
      const allowedExtensionsSupportDocs = /(\.bmp|\.docx|\.htm|\.jpg|\.jpeg|\.odt|\.pdf|\.png|\.raw|\.rtf|\.tiff|\.xml|\.txt)$/i;
      const isAllowedExtension =
        attachmentType === 'mms'
          ? allowedExtensionsMms.test(file.name)
          : allowedExtensionsSupportDocs.test(file.name);

      const isAllowedFileSize = file.size <= FILE_MAX_SIZE;
      if (!isAllowedFileSize) {
        // file errors
        errors.push('File limit is 10 MB');
      }
      if (!(isAllowedType || isAllowedExtension)) {
        errors.push('file type not allowed');
      }
      setFileErrors(errors);
      if (isAllowedFileSize && (isAllowedType || isAllowedExtension)) {
        // start loading snackbar
        setSnackbar({
          onAction: () => {
            controller.abort();
            closeSnackbar();
          },
          actionType: ATTACHMENTS_IN_PROGRESS,
          actionLabel: 'Cancel',
          autoHideDuration: 1000000,
          message: file.name,
          severity: 'info',
        });

        if (attachmentType === 'supportingDocument')
          updateFileSupportingDocs
            .mutateAsync(file)
            .then(({ data = null }: { data?: FileAttachments[] }) => {
              if (data === null) return;

              setFilesSupportDocs((files) => {
                return [...files, ...data];
              });
              showSuccessSnackbar();
            })
            .catch((error: AxiosError) => {
              // when the user has cancelled, do not show the
              // retry snackbar.
              if (error.code === AxiosError.ERR_CANCELED) return;
              showErrorSnackbar();
            });
        else if (attachmentType === 'mms') {
          updateFileMMS
            .mutateAsync(file)
            .then(({ data = null }: { data?: FileAttachments[] }) => {
              if (data === null) return;
              setFilesMMS((files) => {
                return [...files, ...data];
              });

              showSuccessSnackbar();
            })
            .catch((error: AxiosError) => {
              // when the user has cancelled, do not show the
              // retry snackbar.
              if (error.code === AxiosError.ERR_CANCELED) return;
              showErrorSnackbar();
            });
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [updateFileMMS, updateFileSupportingDocs],
  );

  const isVisitedStep = (step: number) => {
    return step in state.visitedSteps;
  };
  const onError = (error) => {
    const errorResponse = error as {
      response?: { data?: { displayMessage?: string } };
    };
    setSnackbar({
      message: errorResponse?.response?.data?.displayMessage || errorMessage,
      severity: 'error',
    });
    dispatch(actions.clearLoading());
  };

  const removeFile = useCallback(
    (fileId) => {
      function UpdateFiles(files: FileAttachments[]) {
        const result = files.filter((file) => file.id !== fileId);
        if (!isEqual(files, result)) {
          return result;
        }
        return files;
      }

      if (fileId) {
        deleteFileAttachments
          .mutateAsync(fileId)
          .then(() => {
            // remove file locally
            setFilesSupportDocs(UpdateFiles);
            setFilesMMS(UpdateFiles);
          })
          .catch((e) => {
            setSnackbar({
              message: e.toString(),
              severity: 'error',
            });
          });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [deleteFileAttachments],
  );

  const saveData = (payload: EnablementInfoPayloadV3) => {
    const newValues = { ...values, ...payload, validSubUsecase: true };
    setValues(newValues);
    const callbacks = {
      onError: (error: QueryObserverResult['error']) => {
        onError(error);
      },
    };

    if (payload.taxIdEin) {
      updateCompanyTaxId.mutate({ taxId: payload.taxIdEin });
    }
    if (!smsCampaignStatus || smsCampaignStatus === 'NOT_STARTED') {
      createEnablementInfo.mutate(payload, callbacks);
    } else {
      updateEnablementInfo.mutate(newValues, callbacks);
    }
  };

  const submitData = (payload: EnablementInfoPayloadV3) => {
    const newValues = {
      ...values,
      ...payload,
      validSubUsecase: true,
      useCase: 'LOW_VOLUME',
    };
    setValues(newValues);
    const callbacks = {
      onSuccess: () => {
        void queryClient.invalidateQueries([ENABLEMENT_INFO_KEY]);
        createTCRBrandAndCampaign.mutate(null, {
          onSuccess: () => {
            void refetch();
            dispatch(actions.setSubmitted());
          },
          onError: (error: QueryObserverResult['error']) => {
            onError(error);
          },
        });
      },
      onError: (error: QueryObserverResult['error']) => {
        onError(error);
      },
    };

    if (payload.taxIdEin) {
      updateCompanyTaxId.mutate({ taxId: payload.taxIdEin });
    }

    if (!smsCampaignStatus || smsCampaignStatus === 'NOT_STARTED') {
      createEnablementInfo.mutate(payload, callbacks);
    } else {
      updateEnablementInfo.mutate(newValues, callbacks);
    }
  };

  // Bit of a hack to ensure that the fields in the previous step are triggered (no, 'trigger' doesn't work for untouched fields)
  const triggerFieldsInStep = (step: number) => {
    if (step === 0) return;
    const stepVal = STEPSLIST[step - 1];
    stepVal.fields.forEach((field) => {
      methods.setValue(field, methods.getValues(field), {
        shouldValidate: true,
        shouldTouch: true,
      });
    });
  };

  const handleBackStep = () => {
    dispatch(actions.setLoading());
    triggerFieldsInStep(state.currentStep);
    const StepValues = methods.getValues();
    saveData(StepValues);
    dispatch(actions.setPrev());
  };

  const handleNextStep = () => {
    dispatch(actions.setLoading());
    triggerFieldsInStep(state.currentStep);
    const StepValues = methods.getValues();
    saveData(StepValues);
    dispatch(actions.setNext());
  };

  const handleSetStep = (step: number) => {
    dispatch(actions.setLoading());
    triggerFieldsInStep(state.currentStep);
    const StepValues = methods.getValues();
    saveData(StepValues);

    dispatch(actions.setStep(step));
  };

  const handleResetForm = () => {
    setResetForm(true);
    dispatch(actions.resetForm());
    doCampaignRefetch();
  };

  const doSubmit = () => {
    dispatch(actions.setLoading());
    const StepValues = methods.getValues();
    submitData(StepValues);
  };

  return (
    <WizardContextV3.Provider
      value={{
        isWebsiteRequestEmailSent,
        handleSendEmail,
        totalFiles,
        fileErrors,
        removeFile,
        handleFileSelected,
        filesSupportDocs,
        filesMMS,
        state,
        dispatch,
        isVisitedStep,
        handleNextStep,
        handleSetStep,
        handleBackStep,
        handleResetForm,
        doSubmit,
        enablementInfo,
        isLoadingGetEnablementInfo,
        isLoadingUpsertEnablementInfo:
          createEnablementInfo.isLoading ||
          updateEnablementInfo.isLoading ||
          updateCompanyTaxId.isLoading ||
          createTCRBrandAndCampaign.isLoading,
        declinedWithBrandVerified,
        isDeclined,
        doCampaignRefetch,
        prevSubUseCases,
        setPrevSubUseCases,
        // react-hook-form items to ensure we can use them in other components
        ...methods,
      }}
    >
      {children}
    </WizardContextV3.Provider>
  );
};
